import React from 'react';

// Renders the DP analysis JSON in the browser, schema-agnostically (the same
// contract as the backend's word_generator: any shape renders), with every
// inline page reference — "(s. 14)", "(sid 12)", "[Sida 53]" — turned into a
// button that opens the document viewer at that page.

// Reader-first keys (current contract) come first; the framework-structured
// keys below them are kept so analyses produced under the old contract
// (stored in DynamoDB/S3) keep rendering correctly.
const HEADINGS = {
  vad_planen_innebar: 'Vad planen innebär',
  vad_blir_battre: 'Vad blir bättre',
  vad_riskerar_att_bli_samre: 'Vad riskerar att bli sämre',
  vad_planen_forutsatter: 'Vad planen förutsätter men inte säkrar',
  sakras_genom: 'Säkras genom',
  planen_och_styrdokumenten: 'Planen och kommunens och regionens styrdokument',
  jamforelse: 'Jämförelse med styrdokumenten',
  redovisas_i_planen: 'Redovisas i planen',
  dokument_som_har_ersatts: 'Styrdokument som har ersatts sedan planen togs fram',
  styrdokument: 'Styrdokument som planen har jämförts med',
  vem_gynnas_och_vem_riskerar_att_glommas_bort: 'Vem gynnas – och vem riskerar att glömmas bort',
  gynnas: 'Gynnas',
  riskerar_att_glommas_bort: 'Riskerar att glömmas bort',
  vad_planen_inte_svarar_pa: 'Vad planen inte svarar på',
  fragor_att_stalla_innan_beslut: 'Frågor att ställa innan beslut',
  forslag_till_forbattringar: 'Förslag till förbättringar',
  rubrik: 'Rubrik',
  beskrivning: 'Beskrivning',
  sammanfattning: 'Sammanfattning',
  helhetsbedomning: 'Helhetsbedömning',
  viktigaste_styrkor: 'Viktigaste styrkor',
  viktigaste_brister: 'Viktigaste brister',
  ej_behandlade_omraden: 'Områden som inte behandlas i planen',
  planinformation: 'Planinformation',
  namn: 'Namn', kommun: 'Kommun', plats: 'Plats', syfte: 'Syfte',
  analyserade_dokument: 'Analyserade dokument',
  halsodimensioner: 'Fyra hälsodimensioner',
  kroppslig_halsa: 'Kroppslig hälsa',
  psykologiskt_valbefinnande: 'Psykologiskt välbefinnande',
  social_hallbarhet: 'Social hållbarhet',
  existentiell_halsa: 'Existentiell hälsa',
  urbana_resurser: 'Fem urbana resurser',
  grona_och_bla_miljoer: 'Gröna- och blå miljöer',
  mobilitet_och_rorelse: 'Mobilitet och rörelse',
  bebyggelse_och_bostader: 'Bebyggelse och bostäder',
  offentliga_rum_och_motesplatser: 'Offentliga rum och sociala mötesplatser',
  social_infrastruktur_och_jamlikhet: 'Social infrastruktur och jämlik tillgång',
  gestaltning_och_biofil_design: 'Gestaltning och biofil design',
  trygghet_och_brottsforebyggande: 'Trygghet och brottsförebyggande',
  jamlikhetsanalys: 'Jämlikhetsanalys',
  grupper_som_gynnas: 'Grupper som gynnas',
  grupper_som_riskerar_exkludering: 'Grupper som riskerar exkludering',
  synergier_och_malkonflikter: 'Synergier och målkonflikter',
  tre_fragor: 'Tre frågor innan beslut',
  vilka_halsodimensioner_paverkas: 'Vilka hälsodimensioner påverkas?',
  finns_synergier: 'Finns synergier mellan hälsa, klimat, biologisk mångfald och social hållbarhet?',
  bidrar_till_jamlik_hallbar_stad: 'Bidrar beslutet till en mer jämlik och långsiktigt hållbar stad?',
  prioriterade_rekommendationer: 'Prioriterade rekommendationer',
  kunskapsluckor: 'Kunskapsluckor',
  observationer: 'Observationer',
  styrkor: 'Styrkor', risker: 'Risker',
  brister_och_fallgropar: 'Brister och fallgropar',
  rekommendationer: 'Rekommendationer',
  bedomning: 'Bedömning', analys: 'Analys',
  analysmetod: 'Analysmetod och omfattning',
  metod: 'Metod',
  analyserat_omfang: 'Analyserat omfång',
  rattelser: 'Uppgifter som har lagts till manuellt',
  begransningar: 'Begränsningar',
  format_note: 'Observera',
};

const SKIP_KEYS = new Set(['num_pages', 'analyzed_at']);

const headingFor = (key) =>
  HEADINGS[String(key).trim().toLowerCase()] ||
  String(key).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const isScalar = (v) =>
  v == null || typeof v === 'number' || typeof v === 'boolean' ||
  (typeof v === 'string' && !v.includes('\n') && v.length <= 200);

const fmtScalar = (v) => {
  if (v == null) return '(ingen uppgift)';
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nej';
  return String(v);
};

// "(s. 14)", "(sid 12)", "(sidan 87-88)", "[Sida 53]", "[s. 14]"
const PAGE_REF = /\((?:s\.|sid\.?|sidan|sidorna|sida)\s*\d+[^)]{0,20}\)|\[\s*[Ss](?:\.|ida)\s*\d+[^\]]{0,10}\]/g;

// Split a string on page references and make each one a clickable button.
export const PageRefText = ({ text, onOpenPage }) => {
  const s = String(text ?? '');
  if (!onOpenPage || !PAGE_REF.test(s)) return <>{s}</>;
  PAGE_REF.lastIndex = 0;
  const parts = [];
  let last = 0;
  let m;
  while ((m = PAGE_REF.exec(s)) !== null) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    const ref = m[0];
    const page = parseInt((ref.match(/\d+/) || [])[0], 10);
    parts.push(
      <button key={m.index} type="button" className="dpc-cite"
              data-tip={`Visa sida ${page} i dokumentet`}
              onClick={() => onOpenPage(page)}>
        {ref}
      </button>
    );
    last = m.index + ref.length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return <>{parts}</>;
};

const Node = ({ data, level, onOpenPage }) => {
  if (data == null) return <p className="av-p">(ingen uppgift)</p>;

  if (Array.isArray(data)) {
    return (
      <ul className="av-list">
        {data.map((item, i) => {
          // A finding item ({rubrik, beskrivning}) reads as prose, not as
          // two "Label: value" lines. Extra keys still render generically.
          if (item !== null && typeof item === 'object' && !Array.isArray(item)
              && 'rubrik' in item) {
            const { rubrik, beskrivning, ...rest } = item;
            return (
              <li key={i}>
                <strong>{i + 1}. {String(rubrik ?? '')}</strong>
                {beskrivning != null && <>{' — '}
                  <PageRefText text={beskrivning} onOpenPage={onOpenPage} /></>}
                {Object.keys(rest).length > 0 &&
                  <Node data={rest} level={level} onOpenPage={onOpenPage} />}
              </li>
            );
          }
          return (
            <li key={i}>
              {typeof item === 'object' && item !== null
                ? <Node data={item} level={level} onOpenPage={onOpenPage} />
                : <PageRefText text={item} onOpenPage={onOpenPage} />}
            </li>
          );
        })}
      </ul>
    );
  }

  if (typeof data === 'object') {
    return (
      <>
        {Object.entries(data).map(([key, value]) => {
          if (level === 1 && SKIP_KEYS.has(key)) return null;
          if (level >= 2 && isScalar(value)) {
            return (
              <p key={key} className="av-p">
                <strong>{headingFor(key)}:</strong>{' '}
                <PageRefText text={fmtScalar(value)} onOpenPage={onOpenPage} />
              </p>
            );
          }
          const H = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5';
          return (
            <React.Fragment key={key}>
              <H className={`av-h av-h${Math.min(level, 3)}`}>{headingFor(key)}</H>
              <Node data={value} level={level + 1} onOpenPage={onOpenPage} />
            </React.Fragment>
          );
        })}
      </>
    );
  }

  return (
    <div className="av-p">
      {String(data).split('\n').filter((l) => l.trim()).map((line, i) => (
        <p key={i}><PageRefText text={line.trim()} onOpenPage={onOpenPage} /></p>
      ))}
    </div>
  );
};

const AnalysisView = ({ result, onOpenPage, problemkartaUrl }) => {
  if (!result) return null;
  if (typeof result !== 'object' || Array.isArray(result)) {
    return (
      <div className="av">
        <Node data={result} level={1} onOpenPage={onOpenPage} />
      </div>
    );
  }
  // Top level rendered inline (same layout Node produces at level 1) so the
  // problemkarta can be injected right after the risk section its numbered
  // markers refer to.
  return (
    <div className="av">
      {Object.entries(result)
        .filter(([key]) => !SKIP_KEYS.has(key))
        .map(([key, value], idx) => {
        return (
          <React.Fragment key={key}>
            <h3 className="av-h av-h1">{idx + 1}. {headingFor(key)}</h3>
            <Node data={value} level={2} onOpenPage={onOpenPage} />
            {key === 'vad_riskerar_att_bli_samre' && problemkartaUrl && (
              <figure className="av-problemkarta">
                <img src={problemkartaUrl} alt="Problemkarta" />
                <figcaption>
                  Problemkarta: siffrorna markerar var punkterna ovan hör hemma.
                  Punkter utan markör gäller hela planområdet eller går inte att
                  platsbestämma på kartan.
                </figcaption>
              </figure>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AnalysisView;
